import GroupObject from "../objects/group"
import Calc from "@/common/util/calculator"
import BaseFixmethod from "@/signomatic/app/fixmethods/base"
import CircleObject from "@/signomatic/app/objects/circle"

export default class HangingFixmethod extends BaseFixmethod {
  get externalTop() {
    const positions = this.positions()
    return positions.length === 1 && !!positions[0].center
  }

  get externalGap() {
    return this.externalTop ? 20 / 2 : 0
  }

  get externalTopPadding() {
    return this.externalTop ? Calc.mmToPx(20 / 2) : 0
  }

  topDrawable() {
    const { widthPx, heightPx } = this.state
    const items = []
    const outerWidth = 1 / this.drawables.scale

    const holeStroke = "#aaa"
    const holeFill = "#F5F7F6"

    const positions = this.positions()

    if (positions.length === 1 && positions[0].center) {
      const radius = Calc.mmToPx(20 / 2)
      const holeRadius = Calc.mmToPx(6 / 2)
      const holeTop = positions[0].center

      items.push(
        new CircleObject({
          radius: holeRadius,
          left: 0 - holeRadius,
          top: 0 - radius + Calc.mmToPx(holeTop),
          stroke: holeStroke,
          strokeWidth: outerWidth,
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
          fill: holeFill,
        }).addClass("hang", "hanging")
      )

      // stretch out
      items.push(
        new CircleObject({
          radius: holeRadius,
          left: 0 - holeRadius,
          top: heightPx - radius + Calc.mmToPx(holeTop),
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
          stroke: "transparent",
          strokeWidth: outerWidth,
          fill: "transparent",
        })
      )
    } else {
      let e

      for (e of positions) {
        items.push(
          new CircleObject({
            left: (e.left || 0) - e.diameter / 2,
            top: (e.top || 0) - e.diameter / 2,
            radius: e.diameter / 2,
            stroke: holeStroke,
            strokeWidth: outerWidth,
            fill: holeFill,
          }).addClass("hang", "hanging")
        )
      }

      // stretch group out
      if (e) {
        items.push(
          new CircleObject({
            left: widthPx / 2 - e.diameter / 2,
            top: heightPx - (e.top || 0) - e.diameter / 2,
            radius: e.diameter / 2,
            stroke: "transparent",
            strokeWidth: outerWidth,
            fill: "transparent",
          })
        )
      }
    }

    return [
      new GroupObject(items, {
        left: 0,
        top: 0,
        width: widthPx,
        height: heightPx,
      }),
    ]
  }

  engravingDrawable() {
    const { widthPx, heightPx } = this.state
    const items = []
    const outerWidth = this.drawables.engravingStrokeWidth
    const holeStroke = this.drawables.engravingColor
    const holeFill = "transparent"

    const positions = this.positions()

    if (positions.length === 1 && positions[0].center) {
      const radius = Calc.mmToPx(20 / 2)
      const holeRadius = Calc.mmToPx(6 / 2)
      const holeTop = positions[0].center

      items.push(
        new CircleObject({
          radius: holeRadius,
          left: 0 - holeRadius,
          top: 0 - radius + Calc.mmToPx(holeTop),
          stroke: holeStroke,
          strokeWidth: outerWidth,
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
          fill: holeFill,
        }).addClass("hang", "hanging")
      )

      // stretch out
      items.push(
        new CircleObject({
          radius: holeRadius,
          left: 0 - holeRadius,
          top: heightPx - radius + Calc.mmToPx(holeTop),
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
          stroke: "transparent",
          strokeWidth: 1,
          fill: "transparent",
        })
      )
    } else {
      let e

      for (e of positions) {
        items.push(
          new CircleObject({
            left: (e.left || 0) - e.diameter / 2,
            top: (e.top || 0) - e.diameter / 2,
            radius: e.diameter / 2,
            stroke: holeStroke,
            strokeWidth: outerWidth,
            fill: holeFill,
          }).addClass("hang", "hanging")
        )
      }

      // stretch group out
      if (e) {
        items.push(
          new CircleObject({
            left: widthPx / 2 - e.diameter / 2,
            top: heightPx - (e.top || 0) - e.diameter / 2,
            radius: e.diameter / 2,
            stroke: "transparent",
            strokeWidth: outerWidth,
            fill: "transparent",
          })
        )
      }
    }

    return [
      new GroupObject(items, {
        left: 0,
        top: 0,
        width: widthPx,
        height: heightPx,
      }),
    ]
  }

  autoSizePadding(state: SignState) {
    const { width, widthPx, heightPx, shape } = state
    const fourScrews = width >= 100
    const extra = fourScrews && this.state.shape === "round" ? Math.min(widthPx, heightPx) * 0.1 : 0

    if (width < 149 && (shape === "rect" || shape === "round")) {
      return 0
    }

    return 50 + extra
  }

  protected positions() {
    const pos = []
    const { width, widthPx, heightPx, shape, border, material } = this.state
    const hasBorder = this.drawables.hasBorder
    const signType = this.drawables.signType
    const borderWidth: number = this.drawables.borderWidth

    const uv_plastic = ["forex_sign", "solid_plastic_sign"].includes(signType)

    const minS = Math.min(heightPx, widthPx)
    let diameter = Math.min(Math.max(minS / 9, 8), 24)
    const alu =
      signType === "aluminium_sign" ||
      material === "aluminium_lacquered" ||
      (signType === "double_sided_sign" && material !== "plastic")
    const plastic = uv_plastic || (signType === "double_sided_sign" && material === "plastic")
    const typeEcoboard = signType === "ecoboard"
    const ecoboard = (signType === "double_sided_sign" && material === "ecoboard") || typeEcoboard

    if (alu || plastic || signType === "wood_sign") {
      diameter = Calc.mmToPx(6.15)
    }

    if (signType === "acryl_sign") {
      diameter = Calc.mmToPx(6.3)
    }

    if (this.isStainless) {
      diameter = Calc.mmToPx(3.5)
    }

    if (ecoboard) {
      diameter = Calc.mmToPx(6)
    }

    const center = widthPx / 2
    const relOptions = { maxWidth: 1000, maxHeight: 600 }

    if (["rounded_top", "rounded_sides", "curved_top", "arched_top"].includes(shape)) {
      const twoHoles = width > 199 && shape != "curved_top"
      const twoHoleRelOptions = { minHeight: 199 }
      let distanceSide = 0
      let distanceTop = 0

      if (signType === "plastic_sign") {
        if (twoHoles) {
          if (shape == "arched_top") {
            distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(10), Calc.mmToPx(15), twoHoleRelOptions)
            distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(79), Calc.mmToPx(113), twoHoleRelOptions)
          } else if (shape == "rounded_top") {
            distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(10), Calc.mmToPx(15), twoHoleRelOptions)
            distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(30), Calc.mmToPx(40), twoHoleRelOptions)
          } else if (shape == "rounded_sides") {
            distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(30), Calc.mmToPx(40), twoHoleRelOptions)
            distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(10), Calc.mmToPx(15), twoHoleRelOptions)
          }
        } else if (shape == "curved_top") {
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(4), Calc.mmToPx(35))
        } else {
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(3), Calc.mmToPx(10), { maxHeight: 199 })
        }
      } else {
        // alu, acryl, wood, forex
        if (twoHoles) {
          if (shape == "arched_top") {
            distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(20), Calc.mmToPx(37), twoHoleRelOptions)
            distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(80), Calc.mmToPx(225), twoHoleRelOptions)
          } else if (shape == "rounded_top") {
            distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(14), Calc.mmToPx(17), twoHoleRelOptions)
            distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(33), Calc.mmToPx(80), twoHoleRelOptions)
          } else if (shape == "rounded_sides") {
            distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(30), Calc.mmToPx(80), twoHoleRelOptions)
            distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(8), Calc.mmToPx(10), twoHoleRelOptions)
          }
        } else if (shape == "curved_top") {
          distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(10), Calc.mmToPx(40), { maxHeight: 300 })
        } else {
          distanceTop = Calc.mmToPx(8)
        }
      }

      const left = distanceSide
      const top = distanceTop
      const right = widthPx - distanceSide

      if (twoHoles) {
        pos.push({ left, top, diameter })
        pos.push({ left: right, top, diameter })
      } else {
        pos.push({ left: center, top, diameter })
      }
    } else if (ecoboard) {
      if (shape === "rotated_rect") {
        const top = this.drawables.sizeRelative("height", Calc.mmToPx(10), Calc.mmToPx(80))
        pos.push({ left: center, top, diameter })
      } else if (shape === "rect" || shape === "round") {
        if (width < 150) {
          pos.push({ left: center, top: Calc.mmToPx(8), diameter })
        } else if (shape === "rect") {
          const widthMax = typeEcoboard ? 50 : 45
          const left = this.drawables.sizeRelative("width", Calc.mmToPx(15), Calc.mmToPx(widthMax), { minWidth: 150 })
          const right = widthPx - left
          const top = this.drawables.sizeRelative("height", Calc.mmToPx(8), Calc.mmToPx(25), { minWidth: 150 })

          pos.push({ left, top, diameter })
          pos.push({ left: right, top, diameter })
        } else if (shape === "round") {
          const widthMax = typeEcoboard ? 120 : 110
          const left = this.drawables.sizeRelative("width", Calc.mmToPx(20), Calc.mmToPx(widthMax), { minWidth: 150 })
          const right = widthPx - left
          const top = this.drawables.sizeRelative("height", Calc.mmToPx(10), Calc.mmToPx(30), { minWidth: 150 })

          pos.push({ left, top, diameter })
          pos.push({ left: right, top, diameter })
        }
      } else if (shape === "ellipse" || shape === "stop") {
        const top = this.drawables.sizeRelative("height", Calc.mmToPx(8), Calc.mmToPx(12))
        pos.push({ left: center, top, diameter })
      } else {
        // triangle
        const top = this.drawables.sizeRelative("height", Calc.mmToPx(hasBorder ? 13 : 12), Calc.mmToPx(45))
        pos.push({ left: center, top, diameter })
      }
    } else if (alu) {
      if (shape === "rect" || shape === "round") {
        if (width < 150) {
          pos.push({ left: center, top: Calc.mmToPx(border === "retro" ? 11 : 8), diameter })
        } else if (shape === "rect") {
          const distanceX = border === "retro" ? this.drawables.sizeRelative("width", 8, 13, relOptions) : 13
          const distanceY = 8
          const left = Calc.mmToPx(distanceX)
          const right = widthPx - Calc.mmToPx(distanceX)
          const top = Calc.mmToPx(distanceY)

          pos.push({ left, top, diameter })
          pos.push({ left: right, top, diameter })
        } else {
          // round
          let distanceX = 0
          let distanceY = 0

          if (signType === "aluminium_sign" || material === "aluminium_lacquered") {
            distanceX = this.drawables.sizeRelative("width", 13, 50, relOptions)
            distanceY = this.drawables.sizeRelative("height", 8, 13, relOptions)
          } else if (signType === "double_sided_sign") {
            distanceX = this.drawables.sizeRelative("width", 16, 140, relOptions)
            distanceY = this.drawables.sizeRelative("height", 9, 25, relOptions)
          }
          const left = Calc.mmToPx(distanceX)
          const right = widthPx - Calc.mmToPx(distanceX)
          const top = Calc.mmToPx(distanceY)

          pos.push({ left, top, diameter })
          pos.push({ left: right, top, diameter })
        }
      } else if (shape === "triangle") {
        const min = Calc.mmToPx(hasBorder ? 13 : 11)
        const max = Calc.mmToPx(hasBorder ? 45 : 40)

        const distance = this.drawables.sizeRelative("height", min, max)
        const top = borderWidth + distance

        pos.push({ left: center, top, diameter })
      } else if (shape === "rotated_rect") {
        const top =
          signType === "aluminium_sign"
            ? this.drawables.sizeRelative("height", Calc.mmToPx(10), Calc.mmToPx(25), { maxHeight: 600 })
            : this.drawables.sizeRelative("height", Calc.mmToPx(10), Calc.mmToPx(80))
        pos.push({ left: center, top, diameter })
      } else {
        pos.push({ left: center, top: Calc.mmToPx(8), diameter })
      }
    } else if (signType === "solid_plastic_sign") {
      if (shape === "rect" || shape === "round") {
        if (width < 150) {
          pos.push({ left: center, top: Calc.mmToPx(8), diameter })
        } else if (shape === "rect") {
          const distanceX = this.drawables.sizeRelative("width", 13, 40, { minWidth: 50 })
          const distanceY = this.drawables.sizeRelative("width", 8, 20, { minWidth: 50 })
          const left = Calc.mmToPx(distanceX)
          const right = widthPx - Calc.mmToPx(distanceX)
          const top = Calc.mmToPx(distanceY)

          pos.push({ left, top, diameter })
          pos.push({ left: right, top, diameter })
        } else {
          // round
          const distanceX = this.drawables.sizeRelative("width", 16, 120, { minWidth: 50 })
          const distanceY = this.drawables.sizeRelative("height", 8, 20, { minHeight: 50 })
          const left = Calc.mmToPx(distanceX)
          const right = widthPx - Calc.mmToPx(distanceX)
          const top = Calc.mmToPx(distanceY)

          pos.push({ left, top, diameter })
          pos.push({ left: right, top, diameter })
        }
      } else if (shape === "triangle") {
        const min = Calc.mmToPx(12)
        const max = Calc.mmToPx(40)

        const distance = this.drawables.sizeRelative("height", min, max, { minHeight: 50, maxHeight: 600 })
        const top = borderWidth + distance

        pos.push({ left: center, top, diameter })
      } else if (shape === "rotated_rect") {
        const top = this.drawables.sizeRelative("height", Calc.mmToPx(7), Calc.mmToPx(26), { maxHeight: 600 })
        pos.push({ left: center, top, diameter })
      } else if (shape === "ellipse" || shape === "stop") {
        const top = this.drawables.sizeRelative("height", Calc.mmToPx(7), Calc.mmToPx(12))
        pos.push({ left: center, top, diameter })
      } else {
        pos.push({ left: center, top: Calc.mmToPx(8), diameter })
      }
    } else if (plastic) {
      if (shape === "rect" || shape === "round") {
        if (width < 150) {
          pos.push({ left: center, top: Calc.mmToPx(border === "retro" ? 11 : 8), diameter })
        } else if (shape === "rect") {
          const distanceX = this.drawables.sizeRelative("width", 13, 20, relOptions)
          const distanceY = this.drawables.sizeRelative("width", 8, 10, relOptions)
          const left = Calc.mmToPx(distanceX)
          const right = widthPx - Calc.mmToPx(distanceX)
          const top = Calc.mmToPx(distanceY)

          pos.push({ left, top, diameter })
          pos.push({ left: right, top, diameter })
        } else {
          // round
          const distanceX = this.drawables.sizeRelative("width", 16, 75, relOptions)
          const distanceY = this.drawables.sizeRelative("height", 9, 13, relOptions)
          const left = Calc.mmToPx(distanceX)
          const right = widthPx - Calc.mmToPx(distanceX)
          const top = Calc.mmToPx(distanceY)

          pos.push({ left, top, diameter })
          pos.push({ left: right, top, diameter })
        }
      } else if (shape === "triangle") {
        const min = Calc.mmToPx(hasBorder ? 13 : 12)
        const max = Calc.mmToPx(hasBorder ? 45 : 40)

        const distance = this.drawables.sizeRelative("height", min, max)
        const top = borderWidth + distance

        pos.push({ left: center, top, diameter })
      } else if (shape === "rotated_rect") {
        const min = Calc.mmToPx(10)
        const top = uv_plastic
          ? this.drawables.sizeRelative("height", min, Calc.mmToPx(35), { maxHeight: 600 })
          : this.drawables.sizeRelative("height", min, Calc.mmToPx(80))
        pos.push({ left: center, top, diameter })
      } else {
        pos.push({ left: center, top: Calc.mmToPx(8), diameter })
      }
    } else if (signType === "acryl_sign") {
      if (shape === "ellipse") {
        const distance = this.drawables.sizeRelative("height", 10, 30, relOptions)

        pos.push({ left: center, top: Calc.mmToPx(distance), diameter })
      } else if (width < 150) {
        pos.push({ left: center, top: Calc.mmToPx(border === "retro" ? 11 : 8), diameter })
      } else if (shape === "rect") {
        const distanceX = this.drawables.sizeRelative("width", 13, 40, relOptions)
        const distanceY = this.drawables.sizeRelative("height", 8, 20, relOptions)
        const left = Calc.mmToPx(distanceX)
        const right = widthPx - Calc.mmToPx(distanceX)
        const top = Calc.mmToPx(distanceY)

        pos.push({ left, top, diameter })
        pos.push({ left: right, top, diameter })
      } else {
        const distanceX = this.drawables.sizeRelative("width", 16, 110, relOptions)
        const distanceY = this.drawables.sizeRelative("height", 9, 25, relOptions)
        const left = Calc.mmToPx(distanceX)
        const right = widthPx - Calc.mmToPx(distanceX)
        const top = Calc.mmToPx(distanceY)

        pos.push({ left, top, diameter })
        pos.push({ left: right, top, diameter })
      }
    } else if (signType === "wood_sign") {
      if (shape === "rotated_rect") {
        const top = this.drawables.sizeRelative("height", Calc.mmToPx(10), Calc.mmToPx(50), { maxHeight: 600 })
        pos.push({ left: center, top, diameter })
      } else if (width < 150) {
        pos.push({ left: center, top: Calc.mmToPx(8), diameter })
      } else if (shape === "rect") {
        const distanceX = this.drawables.sizeRelative("width", 13, 40, relOptions)
        const distanceY = this.drawables.sizeRelative("height", 8, 20, relOptions)
        const left = Calc.mmToPx(distanceX)
        const right = widthPx - Calc.mmToPx(distanceX)
        const top = Calc.mmToPx(distanceY)

        pos.push({ left, top, diameter })
        pos.push({ left: right, top, diameter })
      } else if (shape === "round") {
        const distanceX = this.drawables.sizeRelative("width", 16, 110, relOptions)
        const distanceY = this.drawables.sizeRelative("height", 9, 25, relOptions)
        const left = Calc.mmToPx(distanceX)
        const right = widthPx - Calc.mmToPx(distanceX)
        const top = Calc.mmToPx(distanceY)

        pos.push({ left, top, diameter })
        pos.push({ left: right, top, diameter })
      } else if (shape === "ellipse" || shape === "stop") {
        const distance = this.drawables.sizeRelative("height", 8, 20)
        const top = borderWidth + Calc.mmToPx(distance)

        pos.push({ left: center, top, diameter })
      } else if (shape === "triangle") {
        const distance = this.drawables.sizeRelative("height", hasBorder ? 13 : 11, hasBorder ? 50 : 45)
        const top = borderWidth + Calc.mmToPx(distance)

        pos.push({ left: center, top, diameter })
      } else if (shape === "thorny") {
        const distance = this.drawables.sizeRelative("height", 6, 50)
        const top = borderWidth + Calc.mmToPx(distance)

        pos.push({ left: center, top, diameter })
      }
    } else if (this.isStainless) {
      const top = Calc.mmToPx(3.63)

      pos.push({ left: center, top, diameter })
    } else {
      if (shape === "rect" || shape === "round") {
        if (width < 150) {
          pos.push({ center: 7, diameter })
        } else {
          const min = Calc.mmToPx(5)
          const max = Calc.mmToPx(12)
          const sideMult = 2
          const distance = this.drawables.sizeRelative("height", min, max)

          const left = borderWidth + distance * sideMult
          const right = widthPx - borderWidth - distance * sideMult
          const top = borderWidth + distance

          pos.push({ left, top, diameter })
          pos.push({ left: right, top, diameter })
        }
      } else if (shape === "ellipse" || shape === "thorny" || shape === "stop") {
        diameter = Calc.mmToPx(5)
        let min = 0
        let max = 0

        if (shape === "ellipse") {
          min = Calc.mmToPx(hasBorder ? 3 : 4.5)
          max = Calc.mmToPx(6.5)
        } else if (shape === "thorny") {
          min = Calc.mmToPx(5)
          max = Calc.mmToPx(20)
        } else if (shape === "stop") {
          min = Calc.mmToPx(hasBorder ? 3 : 4)
          max = Calc.mmToPx(hasBorder ? 14 : 15)
        }

        const distance = this.drawables.sizeRelative("height", min, max)
        const top = borderWidth + distance

        pos.push({ left: center, top, diameter })
      } else if (shape === "triangle") {
        const min = Calc.mmToPx(11)
        const max = Calc.mmToPx(16)
        const distance = this.drawables.sizeRelative("height", min, max, {
          maxWidth: 600,
          maxHeight: 600,
        })

        const top = distance

        pos.push({ left: center, top, diameter })
      } else if (shape === "rotated_rect") {
        // plastic engrave
        const top = this.drawables.sizeRelative("height", Calc.mmToPx(4), Calc.mmToPx(20))
        pos.push({ left: center, top, diameter })
      }
    }

    return pos
  }
}
